import { axiosClient, axiosNoAuthClient } from '@/shared/apis/config';
import { DtoAuthProviderType } from '@/shared/constants';
import { ApiResponse } from '@/shared/types';
import { CustomQueryOptions } from '@/shared/types/global/react-query/query';
import { useTokenStore } from '@/shared/store/token-store';
import { createAuthQueryScope } from '@/shared/apis/auth-query-scope';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { z } from 'zod';
import {
  getEffectiveOperatorRole,
  parseOperatorOverrideStorageValue,
} from '@/shared/ui/debug/floating-debug-button/operator-role-toggle';

export const accountMeResSchema = z.object({
  id: z.number(),
  email: z.string(),
  /* 0: wkwk, 1: lg, 2: gs */
  tenantId: z.number(),
  name: z.string(),
  nickName: z.string().optional(),
  customerKey: z.string().nullable().optional(),
  profileUrl: z.string().optional(),
  phoneNumber: z.string().nullable().optional(),
  signupFinished: z.boolean(),
  provider: z.nativeEnum(DtoAuthProviderType),
  advertisingAndMarketingConsent: z.boolean(),
  hasOperatorRole: z.boolean(),
  hasDemoRole: z.boolean(),
  hasDemoSwitchRole: z.boolean(),
  isAccessibleAIFunction: z.boolean(),
  hasYoutubeApiKey: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AccountMeRes = z.infer<typeof accountMeResSchema>;

type AccountMeResponse = ApiResponse<AccountMeRes>;

/**
 * 소셜 로그인 플로우에서 사용하는 유저 정보 조회 API
 */
export const accountMeApi = async (accessToken: string) => {
  try {
    const response = await axiosNoAuthClient.get<AccountMeResponse>(
      '/v1.0/account/me',
      {
        headers: {
          Authorization: accessToken,
        },
      },
    );

    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const QueryKeyUseAccountMe = 'accountMe' as const;

export const useAccountMeApi = (
  accessToken: string,
  queryOptions?: CustomQueryOptions<AccountMeResponse>,
) => {
  const { queryKey, ...rest } = queryOptions ?? {};

  return useQuery({
    queryKey: [QueryKeyUseAccountMe, accessToken, ...(queryKey ?? [])],
    queryFn: async () => await accountMeApi(accessToken),
    ...rest,
  });
};

type AccountMeAuthResponse = ApiResponse<AccountMeRes>;

/**
 * 로그인 상태에서 사용하는 유저 정보 조회 API
 */
export const accountMeAuthApi = async () => {
  try {
    const response =
      await axiosClient.get<AccountMeAuthResponse>('/v1.0/account/me');

    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const QueryKeyUseAccountMeAuth = 'accountMeAuth' as const;

export const useAccountMeAuthApi = (
  queryOptions?: CustomQueryOptions<AccountMeAuthResponse>,
) => {
  const { accessToken, switchAccessToken } = useTokenStore();
  const { queryKey, enabled, ...rest } = queryOptions ?? {};
  const authQueryScope = createAuthQueryScope(accessToken, switchAccessToken);

  return useQuery({
    queryKey: [QueryKeyUseAccountMeAuth, authQueryScope, ...(queryKey ?? [])],
    queryFn: async () => await accountMeAuthApi(),
    // accessToken이 없으면 호출하지 않음 (로그아웃 상태, 비로그인 페이지 등)
    enabled: !!accessToken && (enabled ?? true),
    staleTime: 1000 * 60 * 60 * 24, // 1 day
    placeholderData: keepPreviousData,
    retry: false,
    select: (data) => {
      // 일반 유저의 권한 승격은 허용하지 않고, 실제 operator의 OFF 디버깅만 허용한다.
      if (!data.data.hasOperatorRole) return data;

      const operatorOverride = parseOperatorOverrideStorageValue(
        typeof window !== 'undefined'
          ? localStorage.getItem('operator-role-override')
          : null,
      );
      const hasOperatorRole = getEffectiveOperatorRole({
        originalHasOperatorRole: data.data.hasOperatorRole,
        operatorOverride,
      });

      if (hasOperatorRole !== data.data.hasOperatorRole) {
        return {
          ...data,
          data: {
            ...data.data,
            hasOperatorRole,
          },
        };
      }

      return data;
    },
    ...rest,
  });
};
